import { useDispatchAlert } from '../../../utils/customHooks';
import {
  useAssetCreateDesignMutation,
  useGetUploadedAssetMutation,
  useUploadAssetMutation,
} from '../../../services/canva';
import { tokenExpiringInMs } from '../../../utils/functions/common';
import {
  TExchangeAccessTokenResponse,
  TUploadAssetResponse,
} from '../../../utils/types/canva';
import { useState } from 'react';

export const useEditImageHelper = (
  isAuthWindow?: boolean
) => {
  const dispatchAlert = useDispatchAlert();
  const [uploadAsset, { isLoading: isUploadAssetLoading }] =
    useUploadAssetMutation();
  const [
    getUploadedAsset,
    { isLoading: isGetUploadedAssetLoading },
  ] = useGetUploadedAssetMutation();
  const [getAssetRetryCount, setGetAssetRetryCount] =
    useState(0);
  const [getAssetRetryTimerId, setGetAssetRetryTimerId] =
    useState<NodeJS.Timeout>();
  const [
    assetCreateDesign,
    { isLoading: isAssetCreateDesignLoading },
  ] = useAssetCreateDesignMutation();

  const assetCreateDesignApi = (
    access_token: string,
    asset_id: string
  ) => {
    assetCreateDesign({ access_token, asset_id })
      .unwrap()
      .then((res) => {
        const editUrl = res.design?.urls?.edit_url;
        if (editUrl) {
          isAuthWindow
            ? window.location.replace(editUrl)
            : window.open(editUrl);
        } else {
          dispatchAlert(
            'edit url not found in create design response'
          );
        }
      })
      .catch(() => {});
  };

  const handleAssetUploadSuccess = (
    { job }: TUploadAssetResponse,
    access_token: string
  ) => {
    clearTimeout(getAssetRetryTimerId);
    if (job?.status === 'success') {
      setGetAssetRetryCount(0);
      const assetId = job.asset?.id;
      if (assetId) {
        assetCreateDesignApi(access_token, assetId);
      } else {
        dispatchAlert('asset upload asset id not found');
      }
    } else if (job?.status === 'in_progress') {
      const jobId = job.id;
      if (jobId) {
        const timerId = setTimeout(() => {
          setGetAssetRetryCount(getAssetRetryCount + 1);
          getAssetRetryCount <= 10
            ? getUploadedAssetApi(access_token, jobId)
            : setGetAssetRetryCount(0);
        }, 100);
        setGetAssetRetryTimerId(timerId);
      } else {
        dispatchAlert('asset upload job id not found');
      }
    } else {
      setGetAssetRetryCount(0);
      dispatchAlert(
        job?.error?.message || 'asset upload failed'
      );
    }
  };

  const getUploadedAssetApi = (
    access_token: string,
    job_id: string
  ) => {
    getUploadedAsset({ access_token, job_id })
      .unwrap()
      .then((res) => {
        handleAssetUploadSuccess(res, access_token);
      })
      .catch(() => {});
  };

  const uploadAssetApi = (
    access_token: string,
    asset_url: string
  ) => {
    uploadAsset({ access_token, asset_url })
      .unwrap()
      .then((res) => {
        handleAssetUploadSuccess(res, access_token);
      })
      .catch(() => {});
  };

  const editImageAction = (
    access_token: string,
    asset_url: string
  ) => {
    uploadAssetApi(access_token, asset_url);
  };

  return {
    editImageAction,
    isEditImageActionLoading:
      isUploadAssetLoading ||
      isGetUploadedAssetLoading ||
      isAssetCreateDesignLoading,
  };
};

export const getCanvaAuth = () => {
  const localStorageCanvaAuth =
    localStorage.getItem('canvaAuth');
  if (!localStorageCanvaAuth) {
    return { canvaAuth: undefined, isTokenExpired: true };
  }

  const canvaAuth = JSON.parse(localStorageCanvaAuth) as
    | TExchangeAccessTokenResponse
    | undefined;

  return {
    canvaAuth,
    isTokenExpired: tokenExpiringInMs(
      canvaAuth?.access_token,
      5 * 60 * 1000
    ),
  };
};
